/**
 * ApplicationTable Component
 *
 * Displays a table of applications with actions (view, edit, delete, etc.).
 * Follows the same approach as GroupTable for UI consistency.
 */

import React from 'react';
import { CubeIcon, EyeIcon, RocketLaunchIcon, DocumentTextIcon, Cog6ToothIcon, KeyIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { CopyableCode } from '../../../components/ui/CopyableCode';
import { TableSkeleton } from '../../../components/ui/TableSkeleton';
import { EmptyState } from '../../../components/ui/EmptyState';
import type { ApplicationSanitized } from '../../../types/application.types';
import { formatRelativeTime } from '../../../lib/utils/format';

interface ApplicationTableProps {
  applications: ApplicationSanitized[];
  isLoading?: boolean;
  onView: (application: ApplicationSanitized) => void;
  onEdit?: (application: ApplicationSanitized) => void;
  onDelete?: (application: ApplicationSanitized) => void;
  onViewLogs?: (application: ApplicationSanitized) => void;
  onViewDeployments?: (application: ApplicationSanitized) => void;
  onViewPermissions?: (application: ApplicationSanitized) => void;
  onViewEnvVars?: (application: ApplicationSanitized) => void;
  onViewDeploymentTokens?: (application: ApplicationSanitized) => void;
  canEdit?: boolean;
  canDelete?: boolean;
}

/**
 * Get badge variant based on deployment status
 */
const getStatusBadgeVariant = (status: string): 'success' | 'warning' | 'error' | 'info' => {
  switch (status) {
    case 'deployed':
      return 'success';
    case 'deploying':
      return 'warning';
    case 'failed':
      return 'error';
    case 'pending':
    default:
      return 'info';
  }
};

/**
 * Format deployment status for display
 */
const formatStatus = (status: string): string => {
  return status.charAt(0).toUpperCase() + status.slice(1);
};

/**
 * ApplicationTable component
 */
export const ApplicationTable: React.FC<ApplicationTableProps> = ({
  applications,
  isLoading,
  onView,
  onEdit,
  onDelete,
  onViewLogs,
  onViewDeployments,
  onViewEnvVars,
  onViewDeploymentTokens,
  canEdit = false,
  canDelete = false,
}) => {
  if (isLoading) {
    return <TableSkeleton rows={5} columns={7} />;
  }

  if (applications.length === 0) {
    return (
      <EmptyState
        icon={<CubeIcon className="mx-auto h-12 w-12" />}
        title="No applications found"
        description="Get started by creating a new application."
      />
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Application Name
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Code
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Role
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Description
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Status
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Created
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {applications.map((application) => (
            <tr key={application.applicationId} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-10 w-10">
                    <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                      <CubeIcon className="h-5 w-5 text-primary-600" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <div className="text-sm font-medium text-gray-900">
                      {application.applicationName}
                    </div>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <CopyableCode value={application.applicationCode} />
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {application.role ? (
                  <CopyableCode value={application.role.roleCode} />
                ) : (
                  <span className="text-sm text-gray-400">—</span>
                )}
              </td>
              <td className="px-6 py-4">
                <div className="text-sm text-gray-900 max-w-xs truncate">
                  {application.description || (
                    <span className="text-gray-400 italic">No description</span>
                  )}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <Badge variant={getStatusBadgeVariant(application.deploymentStatus)}>
                  {formatStatus(application.deploymentStatus)}
                </Badge>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {formatRelativeTime(application.createdAt)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <div className="flex items-center justify-end gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => onView(application)}
                    title="View Details"
                  >
                    <EyeIcon className="h-4 w-4" />
                  </Button>
                  {onViewDeployments && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => onViewDeployments(application)}
                      title="View Deployments"
                    >
                      <RocketLaunchIcon className="h-4 w-4" />
                    </Button>
                  )}
                  {onViewLogs && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => onViewLogs(application)}
                      title="View Logs"
                    >
                      <DocumentTextIcon className="h-4 w-4" />
                    </Button>
                  )}
                  {canEdit && onViewEnvVars && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => onViewEnvVars(application)}
                      title="Environment Variables"
                    >
                      <Cog6ToothIcon className="h-4 w-4" />
                    </Button>
                  )}
                  {canEdit && onViewDeploymentTokens && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => onViewDeploymentTokens(application)}
                      title="Deployment Tokens"
                    >
                      <KeyIcon className="h-4 w-4" />
                    </Button>
                  )}
                  {canEdit && onEdit && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => onEdit(application)}
                      title="Edit Application"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </Button>
                  )}
                  {canDelete && onDelete && (
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => onDelete(application)}
                      title="Delete Application"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
